import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Edit, Save, Close } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { FreightRate } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { CatalogListToolbar } from '../common/CatalogListToolbar';
import {
  SortableFilterableHeader,
  FreeSoloAutocompleteField,
  getDistinctValues,
} from '../common/SortableFilterableHeader';

// Fletes: catálogo/lookup zona x tramo de capacidad (CLAUDE.md §6/§7).
// Antes vivía como una pestaña dentro de "Google Sheets" (retirado - la
// sync en vivo con Sheets ya no existe para ningún catálogo); esta es su
// propia página, con menú propio, igual que Productos y Mano de Obra.
//
// Worker brief "catalog-list-ui-pattern" (2026-09-05), continuación a
// Fletes: reemplaza la UI de import/búsqueda/orden propia de esta pantalla
// por CatalogListToolbar/SortableFilterableHeader, mismo tratamiento ya
// aplicado a ProductList.tsx. El import CSV/XLS y la edición inline
// (lápiz -> fila editable -> Guardar/Cancelar) ya existían de una ronda
// anterior - no se reconstruyen desde cero, solo se re-envuelven con los
// componentes compartidos (el import pasa de una Card fija a un Dialog
// disparado desde el toolbar, igual que ProductList.tsx).
type SortableColumn = 'zone';
type FilterableColumn = 'zone' | 'estado';

export const FreightManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [freightRates, setFreightRates] = useState<FreightRate[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Búsqueda - esta pantalla no tenía ningún campo de búsqueda antes de esta
  // ronda (era solo import + tabla). Se agrega por consistencia con el resto
  // de las pantallas de catálogo (Productos, Mano de Obra): filtra
  // client-side sobre el dataset ya cargado (la tabla de Fletes es chica -
  // CLAUDE.md §7 confirma 5 códigos reales hoy - así que no hace falta
  // búsqueda server-side ni debounce).
  const [searchTerm, setSearchTerm] = useState('');

  // Ordenamiento y filtro por columna (worker brief "catalog-list-ui-pattern"):
  // exactamente lo pedido - zona (sort + filter) y estado/is_active (filter).
  const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  // Multi-select, OR-combined per column (worker brief
  // "catalog-list-filter-polish", 2026-09-06) - empty array = unfiltered.
  const [columnFilters, setColumnFilters] = useState<Record<FilterableColumn, string[]>>({
    zone: [],
    estado: [],
  });

  // Edición inline (worker brief "admin-inline-edit", ya construida en una
  // ronda anterior): lápiz junto al código -> fila editable -> Guardar/
  // Cancelar. Solo una fila editable a la vez. El control de Estado
  // (is_active) sigue siendo su propio Select - no se toca en esta ronda.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    codigo: '', zone: '', descripcion: '', pesoMax: '', m2Max: '', price: '', currency: 'ARS', is_active: true,
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Importar Fletes (CSV/XLS) - mismo patrón de Dialog que ProductList.tsx,
  // disparado desde el botón "Importar Fletes CSV/XLS" del toolbar en lugar
  // de la Card fija que tenía esta pantalla antes.
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ created: number; updated: number; errors: string[] } | null>(null);

  const loadFreightRates = useCallback(async () => {
    try {
      setError(null);
      const rates = await apiService.freightRates.getAll();
      setFreightRates(rates);
    } catch (err: any) {
      console.error('Error loading freight rates:', err);
      setError('Error al cargar las tarifas de flete.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFreightRates();
  }, [loadFreightRates]);

  // Distintos valores por columna (mismo patrón que ProductList.tsx):
  // alimenta tanto el dropdown de filtro del encabezado como el Autocomplete
  // freeSolo de edición inline de Zona.
  const zoneOptions = getDistinctValues(freightRates, 'zone');
  const getEstadoLabel = (rate: FreightRate): string => (rate.is_active ? 'Activa' : 'Inactiva');
  const estadoOptions = Array.from(new Set(freightRates.map(getEstadoLabel))).sort();

  const handleColumnFilterChange = (column: FilterableColumn, values: string[]) => {
    setColumnFilters(prev => ({ ...prev, [column]: values }));
  };

  const handleSort = (column: SortableColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const matchesSearch = (rate: FreightRate, term: string) => {
    const t = term.toLowerCase();
    return (
      (rate.codigo || '').toLowerCase().includes(t) ||
      rate.zone.toLowerCase().includes(t) ||
      (rate.descripcion || '').toLowerCase().includes(t)
    );
  };

  // Búsqueda, luego filtro por columna, luego ordenamiento.
  const searchedRates = searchTerm.trim()
    ? freightRates.filter((rate) => matchesSearch(rate, searchTerm.trim()))
    : freightRates;

  const filteredRates = searchedRates.filter((rate) => {
    if (columnFilters.zone.length > 0 && !columnFilters.zone.includes(rate.zone)) return false;
    if (columnFilters.estado.length > 0 && !columnFilters.estado.includes(getEstadoLabel(rate))) return false;
    return true;
  });

  const sortedRates = React.useMemo(() => {
    if (sortColumn !== 'zone') return filteredRates;
    const sorted = [...filteredRates].sort((a, b) =>
      a.zone.localeCompare(b.zone, undefined, { numeric: true, sensitivity: 'base' })
    );
    return sortDirection === 'asc' ? sorted : sorted.reverse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRates, sortColumn, sortDirection]);

  const handleStartEdit = (rate: FreightRate) => {
    setEditingId(rate.id);
    setEditError(null);
    setEditForm({
      codigo: rate.codigo ?? '',
      zone: rate.zone ?? '',
      descripcion: rate.descripcion ?? '',
      pesoMax: rate.peso_max != null ? String(rate.peso_max) : '',
      m2Max: rate.m2_max != null ? String(rate.m2_max) : '',
      price: rate.price != null ? String(rate.price) : '',
      currency: rate.currency || 'ARS',
      is_active: rate.is_active,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditError(null);
  };

  const handleEditFieldChange = (field: keyof typeof editForm, value: string | boolean) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async (rate: FreightRate) => {
    if (!editForm.zone.trim()) {
      setEditError('La zona no puede estar vacía');
      return;
    }
    if (editForm.pesoMax.trim() === '' || Number(editForm.pesoMax) < 0) {
      setEditError('Hasta (kg) debe ser un número mayor o igual a 0');
      return;
    }
    if (editForm.m2Max.trim() === '' || Number(editForm.m2Max) < 0) {
      setEditError('Hasta (m²) debe ser un número mayor o igual a 0');
      return;
    }
    if (editForm.price.trim() === '' || Number(editForm.price) < 0) {
      setEditError('El precio debe ser un número mayor o igual a 0');
      return;
    }
    try {
      setSavingEdit(true);
      setEditError(null);
      const updated = await apiService.freightRates.update(rate.id, {
        zone: editForm.zone.trim(),
        codigo: editForm.codigo.trim() || undefined,
        descripcion: editForm.descripcion.trim() || undefined,
        peso_max: Number(editForm.pesoMax),
        m2_max: Number(editForm.m2Max),
        price: Number(editForm.price),
        currency: editForm.currency,
        is_active: editForm.is_active,
      });
      setFreightRates(prev => prev.map(r => (r.id === rate.id ? updated : r)));
      setEditingId(null);
    } catch (error: any) {
      console.error('Error saving freight rate inline edit:', error);
      setEditError(error.response?.data?.detail || 'Error al guardar los cambios');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleOpenImportDialog = () => {
    setImportFile(null);
    setImportResult(null);
    setImportDialog(true);
  };

  const confirmImport = async () => {
    if (!importFile) return;
    try {
      setImporting(true);
      const result = await apiService.freightRates.importFile(importFile);
      setImportResult(result);
      setImportDialog(false);
      await loadFreightRates();
    } catch (err: any) {
      console.error('Error importing freight rates:', err);
      setImportResult({
        created: 0,
        updated: 0,
        errors: [err.response?.data?.detail || 'Error al importar Fletes'],
      });
      setImportDialog(false);
    } finally {
      setImporting(false);
    }
  };

  // Worker brief "catalog-list-ui-pattern": plantilla CSV de arranque para
  // el importador de arriba. Se descarga vía Blob (no un <a href> directo)
  // por el mismo motivo que ProductList.tsx - autenticación por header
  // Bearer, no cookie.
  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.freightRates.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla_fletes.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading template:', err);
      setError('Error al descargar la plantilla');
    }
  };

  // Phase 8.7 Round A, item 7: ya no es admin-only - un `user` regular ve
  // Fletes en modo solo-lectura, igual que Productos y Mano de Obra
  // (CatalogListToolbar ya oculta Importar/Descargar plantilla vía su propio
  // prop `isAdmin`; abajo se gatea además la columna "Acciones").
  const isAdmin = currentUser?.role === 'admin';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <CatalogListToolbar
        title="Fletes"
        itemNoun="Fletes"
        isAdmin={isAdmin}
        onImportClick={handleOpenImportDialog}
        onDownloadTemplate={handleDownloadTemplate}
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Buscar por código, zona o descripción..."
        subtitle={
          <>
            {freightRates.length > 0 ? (
              <>
                Mostrando {sortedRates.length} de {freightRates.length} tarifa{freightRates.length !== 1 ? 's' : ''}
              </>
            ) : (
              'No hay tarifas de flete cargadas para este país'
            )}
            {searchTerm && ` (filtrado por: "${searchTerm}")`}
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {editError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setEditError(null)}>
          {editError}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 24px 0 rgba(60,72,100,0.10)', overflowX: 'auto' }}>
        <Table>
          <TableHead sx={{ backgroundColor: 'rgb(66,154,45)' }}>
            <TableRow>
              <TableCell align="left" sx={{ color: 'white', fontWeight: 'bold' }}>Código</TableCell>
              <SortableFilterableHeader
                label="Zona"
                onSort={() => handleSort('zone')}
                sortActive={sortColumn === 'zone'}
                sortDirection={sortDirection}
                filterOptions={zoneOptions}
                filterValue={columnFilters.zone}
                onFilterChange={(v) => handleColumnFilterChange('zone', v)}
              />
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Hasta (kg)</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Hasta (m²)</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Precio</TableCell>
              <SortableFilterableHeader
                label="Estado"
                filterOptions={estadoOptions}
                filterValue={columnFilters.estado}
                onFilterChange={(v) => handleColumnFilterChange('estado', v)}
              />
              {isAdmin && (
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRates.map((rate) => (
              <TableRow key={rate.id}>
                {editingId === rate.id ? (
                  <>
                    <TableCell>
                      <TextField size="small" variant="standard" value={editForm.codigo}
                        onChange={(e) => handleEditFieldChange('codigo', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <FreeSoloAutocompleteField
                        value={editForm.zone}
                        options={zoneOptions}
                        onChange={(v) => handleEditFieldChange('zone', v)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" variant="standard" value={editForm.descripcion} sx={{ minWidth: 160 }}
                        onChange={(e) => handleEditFieldChange('descripcion', e.target.value)} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" variant="standard" type="number" value={editForm.pesoMax}
                        inputProps={{ step: 1, min: 0 }} sx={{ width: 90 }}
                        onChange={(e) => handleEditFieldChange('pesoMax', e.target.value)} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField size="small" variant="standard" type="number" value={editForm.m2Max}
                        inputProps={{ step: 1, min: 0 }} sx={{ width: 90 }}
                        onChange={(e) => handleEditFieldChange('m2Max', e.target.value)} />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} alignItems="center" justifyContent="flex-end">
                        <TextField size="small" variant="standard" type="number" value={editForm.price}
                          inputProps={{ step: 0.01, min: 0 }} sx={{ width: 90 }}
                          onChange={(e) => handleEditFieldChange('price', e.target.value)} />
                        <Select size="small" variant="standard" value={editForm.currency}
                          onChange={(e) => handleEditFieldChange('currency', e.target.value as string)}>
                          <MenuItem value="ARS">ARS</MenuItem>
                          <MenuItem value="USD">USD</MenuItem>
                        </Select>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Select size="small" variant="standard" value={editForm.is_active ? 'true' : 'false'}
                        onChange={(e) => handleEditFieldChange('is_active', e.target.value === 'true')}>
                        <MenuItem value="true">Activa</MenuItem>
                        <MenuItem value="false">Inactiva</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <IconButton size="small" color="success" title="Guardar cambios"
                          disabled={savingEdit || !editForm.zone.trim()}
                          onClick={() => handleSaveEdit(rate)}>
                          <Save fontSize="small" />
                        </IconButton>
                        <IconButton size="small" title="Cancelar edición" disabled={savingEdit}
                          onClick={handleCancelEdit}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{rate.codigo || '—'}</TableCell>
                    <TableCell>{rate.zone}</TableCell>
                    <TableCell align="center">{rate.descripcion || '—'}</TableCell>
                    <TableCell align="right">{Number(rate.peso_max).toLocaleString('es-AR')}</TableCell>
                    <TableCell align="right">{Number(rate.m2_max).toLocaleString('es-AR')}</TableCell>
                    <TableCell align="right">{rate.currency} {Number(rate.price).toLocaleString('es-AR')}</TableCell>
                    <TableCell>
                      <Chip label={rate.is_active ? 'Activa' : 'Inactiva'} size="small" color={rate.is_active ? 'success' : 'default'} />
                    </TableCell>
                    {isAdmin && (
                      <TableCell align="center">
                        <IconButton size="small" color="primary" title="Editar tarifa"
                          onClick={() => handleStartEdit(rate)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </>
                )}
              </TableRow>
            ))}
            {sortedRates.length === 0 && (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No hay tarifas de flete para mostrar
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de importación de Fletes (CSV/XLS) - CLAUDE.md §7 */}
      <Dialog
        open={importDialog}
        onClose={() => !importing && setImportDialog(false)}
      >
        <DialogTitle>Importar Fletes (CSV/XLS)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Columnas esperadas (no distingue mayúsculas): CODIGO, ZONA, CAPPESO, CAPM2,
            DESCRIPCION, PRECIO, MONEDA, VALIDODESDE.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se actualiza por CODIGO, sin desactivar tarifas que no estén en el archivo -
            desactivar sigue siendo una acción manual.
          </Typography>
          <Button component="label" variant="outlined" fullWidth>
            {importFile ? importFile.name : 'Seleccionar archivo'}
            <input
              type="file"
              hidden
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)} disabled={importing}>
            Cancelar
          </Button>
          <Button onClick={confirmImport} color="primary" variant="contained" disabled={importing || !importFile}>
            {importing ? 'Importando...' : 'Importar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de resultado de importación */}
      <Dialog
        open={importResult !== null}
        onClose={() => setImportResult(null)}
      >
        <DialogTitle>Resultado de la importación</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="success.main">
            • Creadas: {importResult?.created ?? 0}
          </Typography>
          <Typography variant="body2" color="info.main">
            • Actualizadas: {importResult?.updated ?? 0}
          </Typography>
          {importResult && importResult.errors.length > 0 && (
            <>
              <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 'bold' }}>
                • Errores ({importResult.errors.length}):
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
                {importResult.errors.map((err, idx) => (
                  <Typography key={idx} variant="caption" display="block" color="error.main">
                    {err}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FreightManagement;
